import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const POLL_MS = 3_000;
const DELETE_TIMEOUT_MS = 10 * 60 * 1000;

interface DeleteAccepted {
  slug: string;
  deleting: boolean;
  plan: Array<{ kind: string; name: string }>;
  note?: string;
}

interface StateResource {
  kind: string;
  name: string;
  status: string;
}

/**
 * `ghosty delete <slug>` — permanently remove an app from the platform: its
 * live service, address, database, credentials and records. The GitHub repo
 * is NEVER touched (your code stays yours). Destructive, so it refuses to run
 * without --yes (this CLI never prompts — agent/CI convention).
 */
export async function runDelete(
  slug: string,
  options: { dir?: string; json?: boolean; adminUrl?: string; yes?: boolean; wait?: boolean },
): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');
  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  // Guard against "wrong terminal" deletes: when run inside an app folder,
  // the slug must match the app the folder belongs to.
  try {
    const config = readAppConfig(dir);
    if (config.slug !== slug) {
      fail(
        io,
        `This folder is the app "${config.slug}", but you asked to delete "${slug}". ` +
          'Run the command from outside the app folder if that was intentional.',
        1,
        'SLUG_MISMATCH',
      );
    }
  } catch {
    // Not inside an app tree — that's fine; the slug argument stands alone.
  }

  if (options.yes !== true) {
    fail(
      io,
      `Refusing to delete "${slug}" without --yes. This permanently removes the app, ` +
        'its address and its data (the GitHub repository is kept).',
      1,
      'CONFIRM_REQUIRED',
    );
  }

  const client = new ApiClient(resolved.adminUrl, resolved.profile.token);

  let accepted: DeleteAccepted;
  try {
    accepted = await client.post<DeleteAccepted>(`/builder-api/apps/${slug}/delete`);
  } catch (err) {
    const message = (err as Error).message;
    if (/app_not_found|App not found/i.test(message)) {
      if (io.json) emitJson({ ok: true, slug, alreadyDeleted: true });
      else ok(io, `${pc.bold(slug)} is already deleted.`);
      return;
    }
    fail(io, message, 1, 'DELETE_REJECTED');
  }

  if (io.json) emitJson({ event: 'deleting', slug, plan: accepted.plan });
  else note(io, `Deleting ${slug}… (your GitHub repository is kept)`);

  if (options.wait === false) {
    if (io.json) emitJson({ ok: true, slug, accepted: true, waiting: false });
    else human(io, 'Deletion continues in the background. Check with `ghosty state`.');
    return;
  }

  // Poll the teardown steps; 404 / zero rows = fully deleted.
  const deadline = Date.now() + DELETE_TIMEOUT_MS;
  const printed = new Set<string>();
  for (;;) {
    if (Date.now() > deadline) {
      fail(io, 'Deletion timed out after 10 minutes. Re-run `ghosty delete` to resume it.', 1, 'DELETE_TIMEOUT');
    }
    await sleep(POLL_MS);
    let resources: StateResource[];
    try {
      ({ resources } = await client.get<{ resources: StateResource[] }>(`/blocks/state/${slug}`));
    } catch (err) {
      if (/app_not_found|App not found|\(404\)/i.test((err as Error).message)) break; // gone = done
      continue; // transient; keep polling
    }
    const rows = resources.filter((r) => r.kind.startsWith('teardown.'));
    if (resources.length === 0) break;

    for (const row of rows) {
      const key = `${row.kind}:${row.status}`;
      if (row.status === 'ready' && !printed.has(key)) {
        printed.add(key);
        if (!io.json) human(io, `  ${pc.green('✔')} ${row.kind.replace('teardown.', '')}`);
      }
    }
    const failed = rows.find((r) => r.status === 'failed');
    if (failed) {
      fail(
        io,
        `Deletion stopped at "${failed.kind}". Re-run \`ghosty delete ${slug} --yes\` to retry — it picks up where it left off.`,
        1,
        'DELETE_FAILED',
      );
    }
  }

  if (io.json) {
    emitJson({ ok: true, slug, deleted: true });
    return;
  }
  human(io);
  ok(io, `${pc.bold(slug)} deleted. Your GitHub repository was kept.`);
  human(io);
}
